import './underwriting.quote.bind.modal.scss';
import InfoIcon from '@mui/icons-material/Info';
import * as Yup from 'yup';

import {
  Box,
  IconButton,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  styled,
} from '@mui/material';
import { CoverageUuid, EndorsementUuid } from '@converge/data';
import {
  FormCheckBoxField,
  NewConvergeButton,
  NewDateField,
} from '../../../presentation';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import {
  GET_QUOTES,
  QuoteDetails,
  QuoteStatus,
  SubmissionType,
  canCoverageHaveRetroactiveDate,
  canEndorsementHaveRetroactiveDate,
  useBindQuoteMutation,
} from '@converge/graphql';
import { addYear, formatDollarAmount } from '@converge/utils';
import { useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { convergeLogger } from '@converge/config';
import moment from 'moment-timezone';
import { underwritingQuoteToUpsertVariables } from './utils';
import { useConvergeSnackBar } from '../../../contexts';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { yupResolver } from '@hookform/resolvers/yup';
import { modalCommonStyle } from '../../entity/utils';

const StyledTable = styled(Table)(() => ({
  width: '100%',
  marginBottom: '10px',
  paddingLeft: '10px',
  '& thead tr th': {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  '& thead tr th,td': {
    padding: '2px 10px 2px 10px',
    border: 'none',
  },
}));

interface IUnderwritingQuoteBindModel {
  showBindDialog: boolean;
  propertyAndCasualtyLicenseExpired: boolean | undefined | null;
  surplusLinesTaxLicenseExpired: boolean | undefined | null;
  setBindQuoteDialog: (value: boolean) => void;
  quote: QuoteDetails;
  submissionType: SubmissionType;
}

const customDate = (errorMessage: string) => {
  return Yup.date()
    .nullable()
    .transform((val, originalValue) => {
      return !originalValue
        ? null
        : new Date(
            moment.isMoment(originalValue)
              ? (originalValue as moment.Moment).toDate()
              : new Date(originalValue)
          );
    })
    .typeError(errorMessage)
    .required(errorMessage);
};

type BindUseCase = 'standard' | 'shortTerm' | 'longTerm';

export const UnderwritingQuoteBindModel = ({
  showBindDialog = false,
  setBindQuoteDialog,
  quote,
  submissionType,
  propertyAndCasualtyLicenseExpired,
  surplusLinesTaxLicenseExpired,
}: IUnderwritingQuoteBindModel) => {
  const { notify, closeSnackbar } = useConvergeSnackBar();
  const { submission } = useUnderwritingSubmissionStateContext();

  const formMethods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        policyStartDate: customDate('Effective date must be set'),
        policyEndDate: customDate('Expiry date must be set'),
      })
    ),
    shouldUseNativeValidation: false,
    defaultValues: {
      policyStartDate: submission?.policyStartDate,
      policyEndDate: submission?.policyEndDate,
      coverages: [...quote.coverages.edges].filter(({ node: coverage }) =>
        canCoverageHaveRetroactiveDate(coverage.uuid as CoverageUuid)
      ),
      endorsements: [...quote.endorsements.edges].filter(
        ({ node: endorsement }) =>
          canEndorsementHaveRetroactiveDate(endorsement.uuid as EndorsementUuid)
      ),
    },
  });

  const { handleSubmit, watch } = formMethods;

  const coverageFields = watch('coverages');
  const endorsementFields = watch('endorsements');
  const policyStartDate = watch('policyStartDate');
  const policyEndDate = watch('policyEndDate');

  const { dirtyFields } = useFormState({ control: formMethods.control });

  const { bindQuote, loading: bindQuoteIsLoading } = useBindQuoteMutation();
  const isMutating = bindQuoteIsLoading;

  // Determine license warning message (use case 2 trigger)
  let licenseWarningContent = '';
  if (
    surplusLinesTaxLicenseExpired === true &&
    propertyAndCasualtyLicenseExpired === true
  ) {
    licenseWarningContent =
      'The licenses associated with this submission have expired.';
  } else if (surplusLinesTaxLicenseExpired === true) {
    licenseWarningContent =
      'The Surplus Line Tax License associated with this submission has expired.';
  } else if (propertyAndCasualtyLicenseExpired === true) {
    licenseWarningContent =
      'The Property and Casualty License associated with this submission has expired.';
  } else if (
    propertyAndCasualtyLicenseExpired === undefined &&
    surplusLinesTaxLicenseExpired === undefined
  ) {
    licenseWarningContent =
      'There are no licenses associated with this submission.';
  }

  // Determine term length against submission dates
  const submissionStart = moment.utc(submission?.policyStartDate);
  const submissionEnd = moment.utc(submission?.policyEndDate);
  const expected12MonthEnd = submissionStart.clone().add(12, 'months');
  const termIsLessThan12Months = submissionEnd.isBefore(
    expected12MonthEnd,
    'day'
  );
  const termIsMoreThan12Months = submissionEnd.isAfter(
    expected12MonthEnd,
    'day'
  );

  const useCase: BindUseCase = (() => {
    if (termIsLessThan12Months) return 'shortTerm';
    if (termIsMoreThan12Months) return 'longTerm';
    return 'standard';
  })();

  const hasLicenseWarning = licenseWarningContent.length > 0;

  const [licenseAcknowledged, setLicenseAcknowledged] = useState(false);

  const handleBind = useCallback(async () => {
    const notificationId = notify('Binding quote option...', {
      variant: 'info',
    });
    try {
      const filteredEndorsementEdges = quote.endorsements.edges.filter(
        (endorsement) =>
          !endorsementFields.some(
            (field) => field.node.uuid === endorsement.node.uuid
          )
      );
      await bindQuote({
        variables: {
          boundQuoteUuid: quote?.uuid,
          boundQuoteStatus: QuoteStatus.Bound,
          submissionDetails: {
            uuid: submission.uuid,
            policyStartDate,
            policyEndDate,
          },
          ...underwritingQuoteToUpsertVariables({
            submissionUuid: submission?.uuid || '',
            quote: {
              ...quote,
              coverages: { ...quote.coverages, edges: [...coverageFields] },
              endorsements: {
                edges: [...endorsementFields, ...filteredEndorsementEdges],
              },
            },
          }),
        },
        onCompleted: () => setBindQuoteDialog(false),
        update: (cache, { data: updatedData }) => {
          if (updatedData?.bindQuote?.uuid) {
            const data = cache.readQuery({ query: GET_QUOTES });
            cache.writeQuery({
              query: GET_QUOTES,
              data: {
                ...data,
                quotes: {
                  ...data?.quotes,
                  edges: {
                    ...data?.quotes.edges,
                    ...data?.quotes.edges.map((q) => {
                      if (q?.node?.uuid === quote.uuid) {
                        return {
                          ...q,
                          node: { ...q?.node, ...updatedData?.bindQuote },
                        };
                      }
                      return q;
                    }),
                  },
                },
              },
            });
          }
        },
      });
      notify('Quote option bound successfully', { variant: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : error;
      convergeLogger.error(`Failed to bind quote: ${errorMessage}`);
      convergeLogger.error(error);
      notify(`Failed to bind quote: ${errorMessage}`, { variant: 'error' });
      throw error;
    } finally {
      closeSnackbar(notificationId);
    }
  }, [
    bindQuote,
    closeSnackbar,
    coverageFields,
    endorsementFields,
    notify,
    policyEndDate,
    policyStartDate,
    quote,
    setBindQuoteDialog,
    submission.uuid,
  ]);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      formMethods.setValue(
        'policyEndDate',
        moment(addYear(moment(date).utc())).toISOString(),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
    },
    [formMethods]
  );

  const isConfirmDisabled = hasLicenseWarning && !licenseAcknowledged;

  return (
    <Modal
      open={showBindDialog}
      onClose={() => setBindQuoteDialog(false)}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <FormProvider {...formMethods}>
        <Box
          data-testid="bind-quote-modal"
          sx={modalCommonStyle({
            width: 800,
            height:
              useCase !== 'standard' && hasLicenseWarning
                ? 530
                : useCase !== 'standard' || hasLicenseWarning
                ? 480
                : 399,
          })}
        >
          <IconButton
            aria-label="close"
            data-testid="close-modal-button"
            onClick={() => setBindQuoteDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'black' }}
          >
            <CloseIcon />
          </IconButton>

          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            <Box className="main-title-bind">Bind Quote</Box>
          </Typography>
          <Box className="main-description">
            Review the quote details below before issuing this binder.
          </Box>

          <Stack spacing={1}>
            <Typography
              fontSize="16px"
              fontWeight="bold"
              sx={{ paddingBottom: '10px' }}
            >
              {`${quote.referenceId}: ${quote.name}`}
            </Typography>

            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell
                    className="binder-table-header-modal"
                    data-testid="limit-header"
                  >
                    Limit
                  </TableCell>
                  <TableCell
                    className="binder-table-header-modal"
                    data-testid="retention-header"
                  >
                    {submissionType === SubmissionType.Primary
                      ? 'Retention'
                      : 'Attachment point'}
                  </TableCell>
                  <TableCell
                    className="binder-table-header-modal"
                    data-testid="premium-header"
                  >
                    Premium
                  </TableCell>
                  <TableCell
                    className="binder-table-header-modal"
                    data-testid="effective-date-header"
                    sx={{ width: useCase === 'standard' ? '25%' : undefined }}
                  >
                    Effective Date
                  </TableCell>
                  <TableCell
                    className="binder-table-header-modal"
                    data-testid="expiration-date-header"
                    sx={{
                      paddingLeft:
                        useCase === 'standard' ? '24px !important' : undefined,
                    }}
                  >
                    Expiration Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="binder-table-body-modal">
                <TableRow>
                  <TableCell data-testid="limit-value">
                    {submissionType === SubmissionType.Primary
                      ? formatDollarAmount(quote.limit)
                      : formatDollarAmount(quote.excessConvergeLimit)}
                  </TableCell>
                  <TableCell data-testid="retention-value">
                    {submissionType === SubmissionType.Primary
                      ? formatDollarAmount(quote.retention)
                      : formatDollarAmount(quote.excessConvergeAttachmentPoint)}
                  </TableCell>
                  <TableCell data-testid="premium-value">
                    {submissionType === SubmissionType.Primary
                      ? formatDollarAmount(quote.quotedPremium)
                      : formatDollarAmount(quote.excessConvergeQuotedPremium)}
                  </TableCell>

                  {/* Effective Date: editable only in standard (no term issues) */}
                  <TableCell sx={{ verticalAlign: 'middle' }}>
                    {useCase === 'standard' ? (
                      <form>
                        <NewDateField
                          name="policyStartDate"
                          containerWidth={12}
                          onDateChange={handleDateChange}
                          dataTestId="effective-date-input"
                        />
                      </form>
                    ) : (
                      <Box
                        className="binder-table-body-modal"
                        data-testid="effective-date-value"
                      >
                        {policyStartDate
                          ? moment.utc(policyStartDate).format('MM/DD/YYYY')
                          : '—'}
                      </Box>
                    )}
                  </TableCell>

                  {/* Expiration Date: always read-only, auto-derived from effective date */}
                  <TableCell
                    sx={{
                      verticalAlign: 'middle',
                      paddingLeft:
                        useCase === 'standard' ? '24px !important' : undefined,
                    }}
                  >
                    <Box
                      className="binder-table-body-modal"
                      data-testid="expiration-date-value"
                    >
                      {policyEndDate
                        ? moment.utc(policyEndDate).format('MM/DD/YYYY')
                        : '—'}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </StyledTable>

            {/* Term length warning — blue info banner, shown first */}
            {useCase === 'shortTerm' && (
              <Box
                className="less-or-more-than-12-months-container"
                data-testid="short-term-warning-info"
              >
                <InfoIcon className="icon-less-or-more-than-12-months-message" />
                <Box className="less-or-more-than-12-months-message">
                  Please note that the term for this policy is{' '}
                  <span style={{ fontWeight: '700' }}>less than 12 months</span>
                  . If you need to update the effective date or expiration date,
                  cancel and return to the overview page to proceed.
                </Box>
              </Box>
            )}
            {useCase === 'longTerm' && (
              <Box
                className="less-or-more-than-12-months-container"
                data-testid="long-term-warning-info"
              >
                <InfoIcon className="icon-less-or-more-than-12-months-message" />
                <Box className="less-or-more-than-12-months-message">
                  Please note that the term for this policy is{' '}
                  <span style={{ fontWeight: '700' }}>more than 12 months</span>
                  . If you need to update the effective date or expiration date,
                  cancel and return to the overview page to proceed.
                </Box>
              </Box>
            )}

            {/* License warning — red, with acknowledgement checkbox */}
            {hasLicenseWarning && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  mt: hasLicenseWarning && useCase === 'standard' ? '40px' : 0,
                }}
              >
                <Box
                  className="license-waring-container"
                  data-testid="license-warning-info"
                >
                  <WarningIcon className="icon-license-message" />
                  <Box className="license-waring-message">
                    {licenseWarningContent}
                  </Box>
                </Box>
                <FormCheckBoxField
                  customFont="14px"
                  name=""
                  label="I acknowledge binding this submission without license information"
                  handleChange={(e) => setLicenseAcknowledged(e.target.checked)}
                  dataTestId="acknowledge-license-checkbox"
                />
              </Box>
            )}
          </Stack>

          <Typography id="modal-description" sx={{ mt: 2 }} />

          <Box
            sx={{
              position: 'absolute',
              bottom: 27,
              right: 27,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Box className="cancel-binder-modal-btn-container">
              <NewConvergeButton
                variant="outlined"
                onClick={() => setBindQuoteDialog(false)}
                label="CANCEL"
                dataTestId="cancel-button"
              />
            </Box>
            <NewConvergeButton
              disabled={isConfirmDisabled}
              loading={isMutating}
              onClick={() => {
                handleSubmit(async (modifiedData) => {
                  convergeLogger.debug({ dirtyFields, modifiedData });
                  await handleBind();
                })();
              }}
              variant="contained"
              autoFocus
              label="CONFIRM"
              dataTestId="confirm-bind-button"
            />
          </Box>
        </Box>
      </FormProvider>
    </Modal>
  );
};
